import { useAuthStore } from '../../../../../store/useAuthStore';

const CACHE_PREFIX = 'salespetro:my-station:ai:v2';
const CACHE_TTL_MS = 12 * 60 * 60 * 1000;

export type MyStationAiCacheKind =
  | 'weekly-action-plan'
  | 'radar-narrative'
  | 'opportunity-map-narrative'
  | 'evolution-narrative';

interface IMyStationAiCacheEnvelope<T> {
  v: 1;
  storedAt: number;
  data: T;
}

function isStorageAvailable(): boolean {
  try {
    if (typeof window === 'undefined' || !window.sessionStorage) {
      return false;
    }
    const probeKey = `${CACHE_PREFIX}:probe`;
    window.sessionStorage.setItem(probeKey, '1');
    window.sessionStorage.removeItem(probeKey);
    return true;
  } catch {
    return false;
  }
}

function resolveUserScope(): string | null {
  return useAuthStore.getState().user?.id ?? null;
}

function sanitizeScope(scopeKey: string): string {
  return scopeKey.replace(/[^a-zA-Z0-9:_-]/g, '_').slice(0, 80);
}

export function buildMyStationAiCacheKey(
  kind: MyStationAiCacheKind,
  reportWeekId: string,
  scopeKey: string,
  userId: string
): string {
  return `${CACHE_PREFIX}:${userId}:${reportWeekId}:${kind}:${sanitizeScope(scopeKey)}`;
}

function resolveCacheKey(
  kind: MyStationAiCacheKind,
  reportWeekId: string,
  scopeKey: string
): string | null {
  const userId = resolveUserScope();
  if (!userId) {
    return null;
  }
  return buildMyStationAiCacheKey(kind, reportWeekId, scopeKey, userId);
}

function isFresh(storedAt: number): boolean {
  return Date.now() - storedAt <= CACHE_TTL_MS;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isNarrativeSection(value: unknown): value is { title: string; body: string } {
  return isRecord(value) && typeof value.title === 'string' && typeof value.body === 'string';
}

function isNarrativeData(value: unknown): value is { narrative: string; sections?: unknown } {
  if (!isRecord(value) || typeof value.narrative !== 'string') {
    return false;
  }
  const sections = value.sections;
  if (sections === undefined) {
    return true;
  }
  return Array.isArray(sections) && sections.every(isNarrativeSection);
}

function isWeeklyActionPlanData(
  value: unknown
): value is { narrative: string; priorities: string[] } {
  if (!isRecord(value) || typeof value.narrative !== 'string') {
    return false;
  }
  const priorities = value.priorities;
  return (
    Array.isArray(priorities) &&
    priorities.every((item): item is string => typeof item === 'string')
  );
}

function validateCachedData(kind: MyStationAiCacheKind, data: unknown): boolean {
  if (kind === 'weekly-action-plan') {
    return isWeeklyActionPlanData(data);
  }
  return isNarrativeData(data);
}

export function readMyStationAiCache<T>(
  kind: MyStationAiCacheKind,
  reportWeekId: string | null | undefined,
  scopeKey: string
): T | null {
  if (!isStorageAvailable() || !reportWeekId) {
    return null;
  }

  const key = resolveCacheKey(kind, reportWeekId, scopeKey);
  if (!key) {
    return null;
  }

  try {
    const raw = window.sessionStorage.getItem(key);
    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw) as IMyStationAiCacheEnvelope<unknown>;
    if (parsed?.v !== 1 || typeof parsed.storedAt !== 'number' || !isFresh(parsed.storedAt)) {
      window.sessionStorage.removeItem(key);
      return null;
    }

    if (!validateCachedData(kind, parsed.data)) {
      window.sessionStorage.removeItem(key);
      return null;
    }

    return parsed.data as T;
  } catch (error) {
    console.error('[MyStationAI] Falha ao ler cache de resumo:', error);
    try {
      window.sessionStorage.removeItem(key);
    } catch {
      // ignore
    }
    return null;
  }
}

export function writeMyStationAiCache<T>(
  kind: MyStationAiCacheKind,
  reportWeekId: string | null | undefined,
  scopeKey: string,
  data: T
): void {
  if (!isStorageAvailable() || !reportWeekId) {
    return;
  }

  const key = resolveCacheKey(kind, reportWeekId, scopeKey);
  if (!key) {
    return;
  }

  try {
    const envelope: IMyStationAiCacheEnvelope<T> = {
      v: 1,
      storedAt: Date.now(),
      data,
    };
    window.sessionStorage.setItem(key, JSON.stringify(envelope));
  } catch (error) {
    console.error('[MyStationAI] Falha ao gravar cache de resumo:', error);
  }
}

export function clearMyStationAiCache(
  kind: MyStationAiCacheKind,
  reportWeekId: string | null | undefined,
  scopeKey: string
): void {
  if (!isStorageAvailable() || !reportWeekId) {
    return;
  }

  const key = resolveCacheKey(kind, reportWeekId, scopeKey);
  if (!key) {
    return;
  }

  try {
    window.sessionStorage.removeItem(key);
  } catch (error) {
    console.error('[MyStationAI] Falha ao limpar cache de resumo:', error);
  }
}

/** Remove todos os resumos IA do Meu Posto (logout / troca de conta). */
export function clearAllMyStationAiCache(): void {
  if (!isStorageAvailable()) {
    return;
  }

  try {
    const keysToRemove: string[] = [];
    for (let index = 0; index < window.sessionStorage.length; index += 1) {
      const key = window.sessionStorage.key(index);
      if (key?.startsWith(`${CACHE_PREFIX}:`)) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach((key) => window.sessionStorage.removeItem(key));
  } catch (error) {
    console.error('[MyStationAI] Falha ao limpar cache de resumos:', error);
  }
}
